import { Fragment, useState } from 'react'
import { Upload } from 'lucide-react'
import { Dialog, Transition } from '@headlessui/react'
import { SubmissionType, useBulkSubmissions } from '../BulkSubmissionContext'
import StepIndicator from './StepIndicator'

export function SubmissionModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const { updateStep, type, setType } = useBulkSubmissions()
  const [file, setFile] = useState<File | null>(null)

  if (!isOpen) return null

  const currentStep = 1

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className='relative z-50'>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
        </Transition.Child>

        {/* Modal */}
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0 scale-95'
          enterTo='opacity-100 scale-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100 scale-100'
          leaveTo='opacity-0 scale-95'
        >
          <div className='fixed inset-0 flex items-center justify-center p-4'>
            <Dialog.Panel className='bg-white rounded-lg w-full max-w-3xl'>
              {/* Header */}
              <Dialog.Title className='p-6 border-b'>
                <span className='text-2xl font-semibold text-gray-800'>
                  Start New Submission
                </span>
              </Dialog.Title>

              {/* Progress Steps */}
              <div className='px-6 pt-6'>
                <StepIndicator currentStep={currentStep} />
              </div>

              {/* Info Box */}
              <div className='mx-6 mb-6 p-4 bg-secondary-purple/10 rounded-lg border border-secondary-purple/20'>
                <h3 className='font-medium text-secondary-purple/90 mb-2'>
                  How does submission work?
                </h3>
                <p className='text-secondary-purple/70 text-sm'>
                  Follow the steps to validate your metadata, upload assets, and
                  complete your bulk submission. Step 1 starts here.
                </p>
              </div>

              {/* Form */}
              <div className='px-6'>
                <div className=''>
                  <label>Email *</label>

                  <input type='email' id='email' />
                </div>
                <div className=''>
                  <label>Submission Type *</label>

                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value as SubmissionType)}
                  >
                    <option value=''>Select submission type</option>
                    <option value='film'>Film</option>
                    <option value='series'>Series</option>
                  </select>
                </div>

                <div className='flex items-center gap-4'>
                  <input
                    type='text'
                    value={file?.name || ''}
                    readOnly
                    className='flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 mb-0'
                    placeholder='No file selected'
                  />
                  <label className='button muted'>
                    <Upload className='w-4 h-4' />
                    {file?.name ? 'Replace File' : 'Add File'}
                    <input
                      type='file'
                      className='hidden'
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className='px-6 py-4 mt-6 flex justify-end gap-4 border-t bg-gray-50'>
                <button onClick={onClose} className='button muted'>
                  Cancel
                </button>
                <button
                  onClick={() => {
                    updateStep(1)
                    onClose()
                  }}
                  disabled={!file}
                  className='button primary'
                >
                  Start Validating
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  )
}
